import { useSearchParams } from "react-router";
import { CreditCard, Download } from "lucide-react";
import { MockDataBanner } from "../components/MockDataBanner";
import { PageHeader, type HeaderTab } from "../components/ui";

/**
 * Billing — what this tenant pays Reflex Labs.
 *
 * Stub only: every figure below is placeholder content so the section has a
 * real shape to design against. Nothing here reads or writes tenant data yet.
 */

type TabKey = "overview" | "invoices";

const TABS: HeaderTab[] = [
  { key: "overview", label: "Overview" },
  { key: "invoices", label: "Invoices", count: 6 },
];

const PLAN = {
  name: "Practice",
  seats: 3,
  perSeat: 79,
  renewsOn: "September 28, 2026",
  includedHours: 40,
  usedHours: 31.5,
  overageRate: 0.99,
};

const INVOICES = [
  { id: "RL-2026-0812", date: "Aug 28, 2026", amount: 237.0, status: "paid" },
  { id: "RL-2026-0744", date: "Jul 28, 2026", amount: 248.88, status: "paid" },
  { id: "RL-2026-0671", date: "Jun 28, 2026", amount: 237.0, status: "paid" },
  { id: "RL-2026-0598", date: "May 28, 2026", amount: 237.0, status: "paid" },
  { id: "RL-2026-0522", date: "Apr 28, 2026", amount: 158.0, status: "paid" },
  { id: "RL-2026-0449", date: "Mar 28, 2026", amount: 158.0, status: "refunded" },
];

const money = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
}) {
  return (
    <div className="bg-paper-hi rounded-lg p-5 shadow-card">
      <div className="text-[11.5px] text-ink-4 font-medium mb-3">{label}</div>
      <div className="font-display text-[28px] font-medium num leading-none">
        {value}
      </div>
      {sub && <div className="mt-2 text-[11.5px] text-ink-4">{sub}</div>}
    </div>
  );
}

export function Billing() {
  const [searchParams] = useSearchParams();
  const tab: TabKey =
    searchParams.get("tab") === "invoices" ? "invoices" : "overview";

  const monthly = PLAN.seats * PLAN.perSeat;
  const overageHours = Math.max(0, PLAN.usedHours - PLAN.includedHours);
  const usedPct = Math.min(100, (PLAN.usedHours / PLAN.includedHours) * 100);

  return (
    <div>
      <PageHeader
        tabsLabel="Billing views"
        tabs={TABS}
        activeTab={tab}
        tabParam="tab"
        defaultTab="overview"
        status={
          tab === "invoices"
            ? `${INVOICES.length} invoices.`
            : `${PLAN.name} plan, ${PLAN.seats} seats, renewing ${PLAN.renewsOn}.`
        }
        actions={
          <>
            <button className="px-3.5 py-2 rounded-md bg-paper-hi text-ink-2 border hair hover:bg-paper-2 text-[13px] flex items-center gap-1.5 transition-colors">
              <Download size={13} strokeWidth={1.8} />
              Download invoices
            </button>
            <button className="px-3.5 py-2 rounded-md bg-tide text-paper-hi text-[13px] font-medium flex items-center gap-1.5 hover:bg-tide-d transition-colors">
              <CreditCard size={13} strokeWidth={2} />
              Update payment method
            </button>
          </>
        }
      />

      <MockDataBanner />

      <div className="px-4 md:px-7 pt-6 pb-20">
        {tab === "overview" ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat
                label="Current plan"
                value={PLAN.name}
                sub={`${money(PLAN.perSeat)} per practitioner / month`}
              />
              <Stat
                label="This cycle"
                value={money(monthly + overageHours * PLAN.overageRate)}
                sub={`${PLAN.seats} seats · renews ${PLAN.renewsOn}`}
              />
              <Stat
                label="Session hours"
                value={
                  <>
                    {PLAN.usedHours}
                    <span className="text-ink-4 text-[18px] font-light">
                      {" "}
                      / {PLAN.includedHours}
                    </span>
                  </>
                }
                sub={
                  overageHours > 0
                    ? `${overageHours.toFixed(1)}h over · ${money(PLAN.overageRate)}/h`
                    : `${(PLAN.includedHours - PLAN.usedHours).toFixed(1)}h remaining`
                }
              />
              <Stat
                label="Payment method"
                value={<span className="text-[20px]">•••• 4242</span>}
                sub="Visa · expires 04/28"
              />
            </div>

            <div className="mt-6 bg-paper-hi rounded-lg p-5 shadow-card">
              <div className="flex items-baseline justify-between gap-3 mb-3">
                <h2 className="text-[13.5px] font-semibold">
                  Included hours used
                </h2>
                <span className="text-[12px] text-ink-4 num">
                  {PLAN.usedHours} of {PLAN.includedHours} hours
                </span>
              </div>
              <div
                role="progressbar"
                aria-valuenow={Math.round(usedPct)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Included session hours used this cycle"
                className="h-2 w-full rounded-full bg-paper-2 overflow-hidden"
              >
                <div
                  className="h-full rounded-full bg-tide"
                  style={{ width: `${usedPct}%` }}
                />
              </div>
              <p className="mt-3 text-[12.5px] text-ink-4">
                Hours beyond the included {PLAN.includedHours} are billed at{" "}
                {money(PLAN.overageRate)} per hour on your next invoice.
              </p>
            </div>
          </>
        ) : (
          <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
            <table className="w-full text-[13.5px]">
              <caption className="sr-only">Invoice history</caption>
              <thead>
                <tr className="hair-b bg-wash text-[11px] font-semibold text-ink-4 uppercase tracking-wider">
                  <th scope="col" className="text-left px-5 py-2.5">
                    Invoice
                  </th>
                  <th scope="col" className="text-left px-5 py-2.5">
                    Date
                  </th>
                  <th scope="col" className="text-left px-5 py-2.5">
                    Status
                  </th>
                  <th scope="col" className="text-right px-5 py-2.5">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {INVOICES.map((inv) => (
                  <tr key={inv.id} className="hair-b last:border-b-0">
                    <td className="px-5 py-3 num text-ink-2">{inv.id}</td>
                    <td className="px-5 py-3 text-ink-3">{inv.date}</td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-flex items-center gap-1.5 text-[12.5px] ${
                          inv.status === "refunded" ? "text-ink-4" : "text-sage"
                        }`}
                      >
                        <span
                          className={`dot ${
                            inv.status === "refunded" ? "bg-ink-5" : "bg-sage"
                          }`}
                        />
                        {inv.status === "refunded" ? "Refunded" : "Paid"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right num text-ink-2">
                      {money(inv.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
